import { useEffect, useState } from 'react';
import { Network } from '@capacitor/network';
import { isNative } from '@/lib/native';

/**
 * Live connectivity. Uses Capacitor Network on device (it reports the real radio state) and the
 * browser's online/offline events on web. Screens use this to render cached data with an offline
 * notice instead of a spinner that never resolves.
 */
export function useOnlineStatus(): boolean {
	const [online, setOnline] = useState(() => (typeof navigator === 'undefined' ? true : navigator.onLine));

	useEffect(() => {
		let removeNativeListener: (() => void) | undefined;

		if (isNative()) {
			Network.getStatus().then((s) => setOnline(s.connected)).catch(() => setOnline(true));
			Network.addListener('networkStatusChange', (s) => setOnline(s.connected)).then((handle) => {
				removeNativeListener = () => handle.remove();
			});
		}

		const onOnline = () => setOnline(true);
		const onOffline = () => setOnline(false);
		window.addEventListener('online', onOnline);
		window.addEventListener('offline', onOffline);

		return () => {
			window.removeEventListener('online', onOnline);
			window.removeEventListener('offline', onOffline);
			removeNativeListener?.();
		};
	}, []);

	return online;
}
